/**
 * Guía: Solicitudes de Asesores.
 *
 * Contenido derivado del capítulo **10** del manual: §0 y §1 (qué comparte
 * con Solicitudes y qué la hace distinta), §3.2 y §3.3 (el contexto de
 * aprobación: la cuenta de la app y los tres bloqueos) y §4 (los dos caminos
 * de cierre).
 *
 * Las anclas son las de `LeadsPage`, que es el componente que las dos
 * pantallas comparten: la mecánica es la misma y el ancla no pertenece a una
 * guía.
 *
 * Lo que justifica una guía propia es el final del embudo. En Solicitudes,
 * convertir sólo vincula una empresa que ya existía; aquí **aprobar crea una
 * persona con acceso al panel y con comisión sobre ventas**.
 */

import type { TourFlow } from '../types';

const ADVISOR_LEADS_PATH = '/advisor-leads';

export const c10SolicitudesDeAsesores: TourFlow = {
  id: 'admin.advisor-leads',
  chapter: '10',
  title: 'Asesores comerciales',
  summary:
    'Aquí aprobar no vincula: crea un colaborador con acceso al panel y comisión sobre ventas.',
  href: ADVISOR_LEADS_PATH,
  estimatedMin: 3,
  steps: [
    {
      id: 'intro',
      title: 'Personas que quieren captar comercios',
      body:
        'Llegan del formulario público, igual que las solicitudes de comercios: mismo ' +
        'embudo de siete estados, misma asignación, mismas notas. Lo que cambia es el ' +
        'final.',
      route: ADVISOR_LEADS_PATH,
    },
    {
      id: 'approve-creates-a-person',
      title: 'Aprobar crea un colaborador',
      body:
        'No estampa un vínculo: da de alta una persona con acceso a este panel, su perfil ' +
        'de asesor y su porcentaje de comisión sobre las ventas de los comercios que capte. ' +
        'Es el paso con más consecuencia de la pantalla, y le llega un correo de bienvenida ' +
        'con el enlace para entrar.',
    },
    {
      id: 'two-paths',
      title: 'Dos caminos de cierre, no uno',
      body:
        'Aprobar crea al colaborador desde cero. Vincular sirve cuando esa persona **ya es** ' +
        'colaborador y sólo hay que atar la solicitud a su ficha. Elegir mal el camino ' +
        'duplica identidades de la misma persona.',
    },
    {
      id: 'blocks',
      title: 'Tres situaciones que impiden aprobar',
      body:
        'Que el correo ya sea de un colaborador —entonces se vincula, no se crea—, que el ' +
        'correo sea de una cuenta de comercio, y que su cuenta de la app ya esté atada a ' +
        'otro colaborador. Cada una lo dice al abrir el formulario, antes de que rellenes ' +
        'nada.',
    },
    {
      id: 'same-email-two-sides',
      title: 'Un correo no puede operar los dos lados',
      body:
        'Ni colaborador interno y cuenta de comercio a la vez. Es la misma frontera que ' +
        'aplica el acceso al panel: quien tenga su propio negocio en la plataforma necesita ' +
        'un correo personal distinto del corporativo.',
    },
    {
      id: 'suggested-name',
      title: 'El reparto del nombre es una sugerencia',
      body:
        'El formulario público pide el nombre completo en un solo campo, así que la ' +
        'aprobación lo parte por una regla que no acierta siempre. Está para corregirlo: ' +
        'revísalo antes de guardar, porque después es el nombre de esa persona en el panel.',
    },
    {
      id: 'app-account',
      title: 'Si ya es cliente de la app, se puede vincular',
      body:
        'La comprobación busca una cuenta con su correo o su teléfono, y dice si además es ' +
        'creador de contenido. Vincularla evita dejar a la misma persona con dos identidades ' +
        'sueltas en la plataforma.',
    },
    {
      id: 'funnel',
      title: 'El resto del embudo es idéntico',
      body:
        'Los siete estados, aplazar con fecha, y que «convertida» no se marca a mano. Si ' +
        'ya viste la guía de Solicitudes, eso ya lo sabes.',
      target: 'admin.leads.table',
      placement: 'top',
      optionalTarget: true,
    },
  ],
};
